import { differenceInHours, formatISO, formatISO9075, isValid } from 'date-fns';
import { useCallback } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

import { formatDate as formatDateEds } from '~/eds/src/utils/formatDate';

import { parseUserDate } from '../../utils';
import { useFormattingIntl } from '../useFormattingIntl/useFormattingIntl';
import {
  DateFormat,
  getReactIntlDateFormatOptions,
  getRelativeTimeOptions,
} from './utils';

const messages = defineMessages({
  proseFormat: {
    id: 'useFormatDate.proseFormat',
    defaultMessage: '{day} day of {month}, {year}',
    description:
      'Formatting dates in the prose format. Example: 1 day of January, 2025',
  },
});

function formatProseDate(
  date: Date,
  intl: IntlShape,
  formattingIntl: IntlShape,
) {
  const day = date.getDate();
  const month = formattingIntl.formatDate(date, { month: 'long' });
  const year = formattingIntl.formatDate(date, { year: 'numeric' });

  return intl.formatMessage(messages.proseFormat, { day, month, year });
}

function formatRelativeDate(date: Date, formattingIntl: IntlShape) {
  const { value, unit } = getRelativeTimeOptions(date);
  return formattingIntl.formatRelativeTime(value, unit, {
    numeric: 'auto',
  });
}

function formatDurationDate(date: Date, formattingIntl: IntlShape) {
  const { value, unit } = getRelativeTimeOptions(date);

  if (differenceInHours(new Date(), date) >= 35) {
    return formattingIntl.formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (value === 0 || (unit === 'second' && Math.abs(value) <= 5)) {
    return formattingIntl.formatRelativeTime(value, unit, {
      numeric: 'auto',
    });
  }

  return formattingIntl.formatRelativeTime(value, unit, {
    numeric: 'always',
    style: 'long',
  });
}

export const useFormatDate = () => {
  const intl = useIntl();
  const overrideIntlLocale = useFormattingIntl();

  /**
   *
   * ISO Standards → International technical specifications (Universal format for APIs, databases, system integration)
   * ICU Standards → Industry standard library for international user experience
   * technical formats follow ISO standards for machine interoperability
   * user formats follow ICU standards for human experience.
   * Universally accepted technical format" is exactly what you want for APIs, databases, and system integration
   */
  return useCallback(
    (
      date: Date | string | number | null | undefined,
      format: DateFormat = 'iso',
    ) => {
      if (!date) return '—'; // Treats 0, null, undefined, '' as "no date"

      const parsedDate = parseUserDate(date);

      // Validate parsed date
      if (!isValid(parsedDate) || !parsedDate) {
        return '—';
      }

      //@ts-ignore fixed_long is not a valid format for useFormatDate, but it is for formatDateEds
      if (format === 'fixed_long') {
        return formatDateEds(parsedDate, format);
      }

      if (format === 'relative') {
        return formatRelativeDate(parsedDate, overrideIntlLocale);
      }

      if (format === 'duration') {
        return formatDurationDate(parsedDate, overrideIntlLocale);
      }

      if (format === 'utc') {
        //2025-02-25T11:41:11Z
        return parsedDate.toISOString().split('.')[0] + 'Z';
      }

      //The below formats (iso, iso_date, iso_datetime, iso_complete) are affected by the timezone, not locale,
      //For technical use only. e.g for API values

      if (format === 'iso_date' || format === 'iso') {
        //2025-02-25
        return formatISO(parsedDate, { representation: 'date' });
      }

      if (format === 'iso_datetime') {
        //2025-02-25 hh:mm:ss
        return formatISO9075(parsedDate);
      }

      if (format === 'iso_complete') {
        //2025-02-25T11:41:11-05:00
        return formatISO(parsedDate, {
          representation: 'complete',
        });
      }

      //TODO: do not prose format. Supported just for workflow builder, not supported by react intl
      if (format === 'prose') {
        return formatProseDate(parsedDate, intl, overrideIntlLocale);
      }

      return overrideIntlLocale.formatDate(
        parsedDate,
        getReactIntlDateFormatOptions(format),
      );
    },
    [intl, overrideIntlLocale],
  );
};
